import { createStore } from '@nile/mangifera/src/StoreManager/store/createStore';
import { ReactNode } from 'react';

interface Tab {
  name: string;
  label: ReactNode;
  component: React.ComponentType<any>;
  value: string;
  mixpanelEvent: string;
}

type TabOrNull = Tab | null;

interface RecommendedSolutionArticle {
  id: string;
  title: string;
  userType: any;
  visibilityType: string;
  loginRequired: boolean;
}

interface RecommendedSolutionReply {
  recommendedReply: string;
  recommendedSolutionArticles: RecommendedSolutionArticle[];
}

type RecommendedSolutionReplyOrNull = RecommendedSolutionReply | null;

interface SearchTerm {
  searchTerm: string;
  searchTermId: string;
  selected?: boolean;
}

interface AIWebSearchRecommendation {
  getAIWebSearchRecommendation: string;
}

interface RecommendedSolution {
  title: string;
  summary: string;
  tickets?: Ticket[];
  kbArticles?: KnowledgeBaseArticle[];
}

interface Ticket {
  ticketId: string;
  displayId: string;
  subject: string;
  clientName: string;
}

interface KnowledgeBaseArticle {
  id: string;
  title: string;
  userType: 'TECHNICIAN' | 'END_USER' | string; // extend as needed
  visibilityType: 'PUBLIC' | 'PRIVATE' | string; // extend as needed
  loginRequired: boolean;
}

// The array type
type RecommendedSolutions = RecommendedSolution[];

const aiRecommendedSolutionsStoreConfig = {
  id: 'aiRecommendedSolutionsStore',
  initialState: {
    showSimilarTickets: null,
    similarTickets: null,
    showWebSearch: null,
    selectedTab: null,
    sendReplyResponse: null,
    sendReplyData: '',
    aiSuggestedProperties: null,
    closureFields: null,
    searchTerms: null,
    webSearchResult: null,
    showSuggestedReply: false,
    // To set recommended solution response
    sendRecommendedSolutionReplyResponse: {},
    sendRecommendedSolutionReplyData: {},

    // For WebSearch
    sendWebSearchReplyResponse: null,
    sendWebSearchReplyData: null,

    aiRecommendedSolutions: null
  },

  methods: (set, get) => ({
    setShowSimilarTickets: (bool: boolean) => {
      set({
        showSimilarTickets: bool
      });
    },
    setSimilarTickets: (data: any) => {
      set({
        similarTickets: data
      });
    },
    setShowWebSearch: (bool: boolean) => {
      set({
        showWebSearch: bool
      });
    },
    setSelectedTab: (tab: TabOrNull, index: number) => {
      const tabData = get()?.selectedTab || {};
      const updatedData = { ...tabData, [index]: tab };
      set({
        selectedTab: updatedData
      });
    },
    setSendReplyResponse: (data: RecommendedSolutionReplyOrNull) => {
      set({
        sendReplyResponse: data
      });
    },
    setSendReplyData: (data: string) => {
      set({
        sendReplyData: data
      });
    },
    setAISuggestedProperties: (data: any) => {
      set({
        aiSuggestedProperties: data
      });
    },
    setClosureFields: (data: any) => {
      set({
        closureFields: data
      });
    },
    setSearchTerms: (data: SearchTerm[]) => {
      set({
        searchTerms: data
      });
    },
    setWebSearchResult: (data: AIWebSearchRecommendation) => {
      set({
        webSearchResult: data
      });
    },
    setShowSuggestedReply: (bool: boolean) => {
      set({
        showSuggestedReply: bool
      });
    },

    setSendRecommendedSolutionReplyData: (id: string, data: string) => {
      const currentData = get()?.sendRecommendedSolutionReplyData || {};
      set({
        sendRecommendedSolutionReplyData: {
          ...currentData,
          [id]: data
        }
      });
    },

    setSendWebSearchReplyData: (data: string) => {
      set({
        sendWebSearchReplyData: data
      });
    },

    setSendRecommendedSolutionReplyResponse: (id: string, data: string) => {
      const currentData = get()?.sendRecommendedSolutionReplyResponse || {};
      set({
        sendRecommendedSolutionReplyResponse: {
          ...currentData,
          [id]: data
        }
      });
    },

    setSendWebSearchReplyResponse: (data: any) => {
      set({
        sendWebSearchReplyResponse: data
      });
    },

    setAiRecommendedSolutions: (data: RecommendedSolutions) => {
      set({
        aiRecommendedSolutions: data
      });
    },
    resetRecommendedSolutionsData: () => {
      set({
        showSimilarTickets: null,
        similarTickets: null,
        showWebSearch: null,
        selectedTab: null,
        sendReplyResponse: null,
        sendReplyData: '',
        aiSuggestedProperties: null,
        closureFields: null,
        searchTerms: null,
        webSearchResult: null,
        showSuggestedReply: false,
        // To set recommended solution response
        sendRecommendedSolutionReplyResponse: {},
        sendRecommendedSolutionReplyData: {},
        // For WebSearch
        sendWebSearchReplyResponse: null,
        sendWebSearchReplyData: null,
        aiRecommendedSolutions: null
      });
    }
  }),
  cache: {
    expiryTime: 300,
    strategy: 'memory',
    tabBehavior: 'persist',
    clearOnRefresh: true
  }
};

export const useAIRecommendedSolutionsStore = createStore(
  aiRecommendedSolutionsStoreConfig
);
